import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../apiClient";

export const supplierInvoiceKeys = {
  all: ["supplier-invoices"] as const,
  list: () => [...supplierInvoiceKeys.all, "list"] as const,
  payables: () => [...supplierInvoiceKeys.all, "payables"] as const,
};

export type SupplierInvoice = {
  gmail_message_id: string;
  attachment_id: string;
  filename: string;
  supplier_domain: string;
  supplier_name: string;
  subject: string;
  date_sent: string;
  size?: number | null;
};

type SupplierInvoicesResponse = {
  items: SupplierInvoice[];
  total: number;
  skip: number;
  limit: number;
  suppliers: string[];
};

export type SupplierInvoicesResult = {
  invoices: SupplierInvoice[];
  suppliers: string[];
};

/** URL for streaming one supplier invoice PDF. */
export const supplierInvoiceFileUrl = (invoice: {
  gmail_message_id: string;
  attachment_id: string;
}) =>
  `/api/v1/supplier_invoices/${encodeURIComponent(
    invoice.gmail_message_id,
  )}/attachments/${encodeURIComponent(invoice.attachment_id)}`;

/**
 * Fetch every supplier invoice, paging until the server is exhausted.
 * Mirrors useAllInvoicesForArchiveQuery — the page groups and filters locally.
 */
export const useSupplierInvoicesQuery = () =>
  useQuery({
    queryKey: supplierInvoiceKeys.list(),
    queryFn: async (): Promise<SupplierInvoicesResult> => {
      const invoices: SupplierInvoice[] = [];
      let suppliers: string[] = [];
      let skip = 0;
      const limit = 500;
      let hasMore = true;

      while (hasMore) {
        const { data } = await apiClient.get<SupplierInvoicesResponse>(
          `/supplier_invoices?skip=${skip}&limit=${limit}`,
        );
        invoices.push(...data.items);
        suppliers = data.suppliers;
        skip += data.items.length;
        hasMore = data.items.length === limit && skip < data.total;
      }

      return { invoices, suppliers };
    },
  });

export type PayableStatus = "pending_approval" | "approved" | "paid";

export type PayableInvoice = {
  id: number;
  vendor_name: string;
  description: string | null;
  notes: string | null;
  invoice_date: string | null;
  amount: number | null;
  currency: string | null;
  status: PayableStatus;
  /** Seeded demo row — there is no real PDF behind it. */
  is_demo: boolean;
  approved_at: string | null;
  paid_at: string | null;
  is_manually_edited: boolean;
  extraction_status: "pending" | "succeeded" | "failed";
  gmail_message_id: string;
  attachment_id: string;
  filename: string;
  subject: string;
  supplier_domain: string;
  email_date_sent: string;
};

export type PayableInvoicesResult = {
  items: PayableInvoice[];
  vendors: string[];
  pending_extraction: number;
};

export const usePayableInvoicesQuery = () =>
  useQuery({
    queryKey: supplierInvoiceKeys.payables(),
    queryFn: async (): Promise<PayableInvoicesResult> => {
      const { data } = await apiClient.get<PayableInvoicesResult>(
        "/supplier_invoices/payables",
      );
      return data;
    },
  });

/**
 * Run one bounded PDF extraction pass. Each invoice costs a mailbox round
 * trip, so the page calls this repeatedly while rows remain pending.
 */
export const useExtractPayableFieldsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (limit: number = 10) => {
      const { data } = await apiClient.post<{
        processed: number;
        pending_extraction: number;
      }>(`/supplier_invoices/payables/extract?limit=${limit}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: supplierInvoiceKeys.payables(),
      });
    },
  });
};

type PayableFieldUpdate = {
  id: number;
  vendor_name?: string;
  description?: string | null;
  notes?: string | null;
  invoice_date?: string | null;
  amount?: number | null;
  currency?: string | null;
};

/** URL for the draft payment file (approved but unpaid invoices, as CSV). */
export const paymentFileDraftUrl = "/api/v1/supplier_invoices/payables/payment-file";

export const useUpdatePayableInvoiceMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...fields }: PayableFieldUpdate) => {
      const { data } = await apiClient.patch<PayableInvoice>(
        `/supplier_invoices/payables/${id}`,
        fields,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: supplierInvoiceKeys.payables(),
      });
    },
  });
};

type PayableAction =
  | "approve"
  | "unapprove"
  | "mark_paid"
  | "unmark_paid";

export const usePayableInvoiceActionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
      paymentDate,
    }: {
      id: number;
      action: PayableAction;
      paymentDate?: string;
    }) => {
      const body =
        action === "mark_paid" && paymentDate
          ? { payment_date: paymentDate }
          : undefined;
      const { data } = await apiClient.post<PayableInvoice>(
        `/supplier_invoices/payables/${id}/${action}`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: supplierInvoiceKeys.payables(),
      });
    },
  });
};
